import React from 'react';

/**
 * Error state component
 * Maps to Req 83 AC 2: error states for every component
 * Maps to Req 83 AC 1: graceful degradation
 * WCAG 2.1 AA compliant with role="alert"
 */
interface ErrorStateProps {
  title?: string;
  message: string;
  onRetry?: () => void;
  retry?: () => void;
  showLastKnownGood?: boolean;
  lastUpdated?: Date;
}

export const ErrorState: React.FC<ErrorStateProps> = ({
  title = 'Something went wrong',
  message,
  retry,
  onRetry,
  showLastKnownGood = false,
  lastUpdated,
}) => {
  const handleRetry = retry || onRetry;
  
  return (
    <div
      className="flex flex-col items-center justify-center p-8"
      role="alert"
      aria-live="assertive"
    >
      <div className="rounded-full bg-rose-100 p-3 dark:bg-rose-900/20">
        <svg
          className="h-6 w-6 text-rose-600 dark:text-rose-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      </div>
      <h3 className="mt-4 text-lg font-semibold text-foreground">
        {title}
      </h3>
      <p className="mt-2 text-sm text-muted-foreground">{message}</p>
      
      {showLastKnownGood && lastUpdated && (
        <div className="mt-4 rounded-md bg-amber-50 p-3 dark:bg-amber-900/20">
          <p className="text-xs text-amber-800 dark:text-amber-200">
            Showing last known good data from{' '}
            {lastUpdated.toLocaleString()}
          </p>
        </div>
      )}

      {handleRetry && (
        <button
          onClick={handleRetry}
          className="mt-6 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          aria-label="Retry loading"
        >
          Try Again
        </button>
      )}
    </div>
  );
};
